import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS, iconForKey, Mark } from "@/lib/theme";
import { apiGet, apiPost, API } from "@/lib/api";
import { Tin, Coin } from "@/components/TinCoin";
import BackHomeBar from "@/components/BackHomeBar";

/**
 * Donation flow:
 *  tin -> categories -> amounts -> payment (Peach widget or sandbox stub) -> thanks
 */
export default function Give({ onDone }) {
  const [step, setStep] = useState("tin"); // tin | categories | amounts | payment | thanks
  const [dropping, setDropping] = useState(false);
  const [categories, setCategories] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [checkout, setCheckout] = useState(null); // {donation_id, checkout_id, widget_script, stub_mode, amount, category}
  const [status, setStatus] = useState(""); // sandbox_completed | success | ...
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet("/donation-categories").then((d) => {
      setCategories(d.categories || []);
      setPresets(d.presets || []);
    }).catch(() => {});
  }, []);

  const tapTin = () => {
    setDropping(true);
    setTimeout(() => {
      setDropping(false);
      setStep("categories");
    }, 900);
  };

  const chooseCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedAmount(null);
    setCustomAmount("");
    setStep("amounts");
  };

  const amount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const proceedToPayment = async () => {
    if (!amount || amount <= 0) return;
    setBusy(true); setError("");
    try {
      const res = await apiPost("/donations/create-payment", {
        category: selectedCategory.id,
        amount,
        is_custom_amount: !!customAmount,
      });
      setCheckout(res);
      setStep("payment");

      // If in stub mode (Peach sandbox creds not yet configured), auto-load the widget script
      if (!res.stub_mode && res.widget_script) {
        const scriptEl = document.createElement("script");
        scriptEl.src = res.widget_script;
        scriptEl.async = true;
        document.body.appendChild(scriptEl);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Payment failed to start");
    } finally {
      setBusy(false);
    }
  };

  const completeStubDonation = async () => {
    if (!checkout) return;
    setBusy(true); setError("");
    try {
      const res = await apiPost(`/donations/simulate-complete/${checkout.donation_id}`);
      setStatus(res.status);
      setDropping(true);
      setStep("thanks");
      setTimeout(() => setDropping(false), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to complete demo donation");
    } finally {
      setBusy(false);
    }
  };

  const backToHome = () => {
    setStep("tin");
    setSelectedCategory(null);
    setSelectedAmount(null);
    setCustomAmount("");
    setCheckout(null);
    setStatus("");
    onDone && onDone();
  };

  return (
    <div className="ls-scroll" data-testid="give-screen" style={{ position: "relative", paddingBottom: 100 }}>
      {step === "tin" && <BackHomeBar title="Give" onHome={backToHome} />}
      {/* Step: TIN */}
      {step === "tin" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 22px 0" }}>
          <div className="font-heading" style={{ fontWeight: 800, fontSize: 24, color: COLORS.ink }}>Give with a coin</div>
          <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginTop: 4, textAlign: "center" }}>
            Tap the tin to choose a cause and amount
          </div>
          <div style={{ height: 110, marginTop: 26 }}>
            <Coin animate={dropping} />
          </div>
          <div style={{ marginTop: 14 }} data-testid="give-tin-tap">
            <Tin onTap={tapTin} shake={dropping} />
          </div>
        </div>
      )}

      {/* Step: CATEGORIES */}
      {step === "categories" && (
        <div className="anim-fadeUp" style={{ padding: "26px 22px 0" }}>
          <button
            onClick={() => setStep("tin")}
            aria-label="Back"
            className="no-tap-highlight"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}
          >
            <Ic d={ICONS.back} color={COLORS.purple} size={28} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <Mark size={52} />
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink, marginTop: 4 }}>Choose a cause</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13 }}>Every mitzvah counts</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`give-category-${c.id}`}
                onClick={() => chooseCategory(c)}
                className="ls-card no-tap-highlight"
                style={{ padding: "18px 12px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 12, background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic d={iconForKey(c.icon)} color={COLORS.plum} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 13.5, color: COLORS.ink, lineHeight: 1.2 }}>{c.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step: AMOUNTS */}
      {step === "amounts" && selectedCategory && (
        <div className="anim-fadeUp" style={{ padding: "26px 22px 100px" }}>
          <button onClick={() => setStep("categories")} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}>
            <Ic d={ICONS.back} color={COLORS.purple} size={28} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <Mark size={48} />
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 18, color: COLORS.purple }}>{selectedCategory.name}</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, marginTop: 2, fontSize: 13 }}>Please select an amount (ZAR)</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {presets.map((a) => {
              const on = selectedAmount === a && !customAmount;
              return (
                <button
                  key={a}
                  data-testid={`give-amount-${a}`}
                  onClick={() => { setSelectedAmount(a); setCustomAmount(""); }}
                  className="no-tap-highlight"
                  style={{
                    padding: "18px 0",
                    borderRadius: 16,
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 21,
                    color: on ? "#fff" : COLORS.purple,
                    background: on ? `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.plum})` : "#fff",
                    border: `2px solid ${on ? COLORS.purple : "#ead9f2"}`,
                    boxShadow: on ? "0 6px 16px rgba(91,42,130,0.3)" : "none",
                    fontFamily: "'Nunito', sans-serif",
                  }}
                >
                  R{a}
                </button>
              );
            })}
          </div>
          <div style={{ height: 1, background: "#e3d8ec", margin: "20px 0 14px" }} />
          <input
            data-testid="give-custom-amount"
            value={customAmount}
            onChange={(e) => { setCustomAmount(e.target.value.replace(/[^0-9.]/g, "")); setSelectedAmount(null); }}
            placeholder="Or enter a custom amount (ZAR)"
            inputMode="decimal"
            className="ls-input"
            style={{ textAlign: "center", fontWeight: 800, fontSize: 17, color: COLORS.purple }}
          />
          {error && <div style={{ color: "#a02040", fontSize: 13, fontWeight: 700, textAlign: "center", marginTop: 10 }}>{error}</div>}
          <button
            data-testid="give-donate-btn"
            className="ls-primary-btn"
            style={{ width: "100%", marginTop: 14, padding: 17, fontSize: 18 }}
            disabled={busy || (!selectedAmount && !customAmount)}
            onClick={proceedToPayment}
          >
            {busy ? "Please wait…" : `Donate R${amount || 0}`}
          </button>
        </div>
      )}

      {/* Step: PAYMENT (widget or stub) */}
      {step === "payment" && checkout && (
        <div className="anim-fadeUp" style={{ padding: "26px 22px 100px" }}>
          <button onClick={() => setStep("amounts")} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}>
            <Ic d={ICONS.back} color={COLORS.purple} size={28} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink }}>Complete payment</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginTop: 4 }}>
              {selectedCategory?.name} · R{checkout.amount}
            </div>
          </div>

          {checkout.stub_mode ? (
            <div className="ls-card" data-testid="give-sandbox-notice" style={{ padding: 20, textAlign: "center" }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: COLORS.goldLight, margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Ic d={ICONS.info} color={COLORS.plum} size={28} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, color: COLORS.ink }}>Peach Payments sandbox mode</div>
              <div style={{ fontSize: 13, color: COLORS.muted, fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>
                The Peach Payments credentials have not been supplied yet.
                The donation record is stored — once <code>PEACH_ENTITY_ID</code> and <code>PEACH_ACCESS_TOKEN</code> are configured in the backend, the hosted Copy&Pay widget will load here automatically.
              </div>
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <button data-testid="give-simulate-complete" className="ls-primary-btn" onClick={completeStubDonation} disabled={busy}>
                  {busy ? "Processing…" : "Simulate successful donation"}
                </button>
                <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600 }}>
                  Reference: {checkout.merchant_tx_id}
                </div>
              </div>
              {error && <div style={{ color: "#a02040", fontSize: 13, fontWeight: 700, marginTop: 10 }}>{error}</div>}
            </div>
          ) : (
            <div className="ls-card" style={{ padding: 20 }}>
              {/* Peach Copy&Pay widget mount point */}
              <form
                action={`${window.location.origin}/donation/return`}
                className="paymentWidgets"
                data-brands="VISA MASTER AMEX"
              ></form>
              <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600, marginTop: 12 }}>
                Reference: {checkout.merchant_tx_id}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step: THANKS */}
      {step === "thanks" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "36px 22px 0" }}>
          <div style={{ height: 110 }}>
            <Coin animate={dropping} />
          </div>
          <Tin />
          <div className="anim-pop" data-testid="give-thanks" style={{ textAlign: "center", marginTop: 22 }}>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 28, color: COLORS.purple }}>Toda Raba!</div>
            <div style={{ color: COLORS.muted, fontWeight: 600, marginTop: 6, padding: "0 24px", lineHeight: 1.5 }}>
              Your {selectedCategory?.name?.toLowerCase()} of R{amount} was received. Tizku l'mitzvot.
            </div>
            <button data-testid="give-back-home" className="ls-gold-btn" style={{ marginTop: 22 }} onClick={backToHome}>
              Back home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
